import { Sparkles, Volume2, Waves } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { LearningSession, SceneTheme } from "@shared/types";

type SpeakingSide = "user" | "ai" | null;
type MotionState = "idle" | "greeting" | "talking" | "listening" | "walking" | "sitting" | "presenting" | "celebrating";

type SceneProps = {
  session: LearningSession;
  speakingSide: SpeakingSide;
  listening: boolean;
  assistantText?: string;
  userText?: string;
  emotion?: string | null;
};

type Vec3 = [number, number, number];
type Mat4 = Float32Array;

type Geometry = {
  position: WebGLBuffer;
  normal: WebGLBuffer;
  count: number;
};

type RenderContext = {
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  cube: Geometry;
  sphere: Geometry;
  cylinder: Geometry;
  uniforms: {
    model: WebGLUniformLocation;
    viewProjection: WebGLUniformLocation;
    color: WebGLUniformLocation;
    emissive: WebGLUniformLocation;
    lightDirection: WebGLUniformLocation;
  };
};

type RigNode = {
  name: string;
  parent: string | null;
  basePosition: Vec3;
  baseRotation: Vec3;
  mesh?: {
    geometry: "cube" | "sphere" | "cylinder";
    offset: Vec3;
    scale: Vec3;
    color: Vec3;
    emissive?: number;
  };
};

type Pose = Record<string, Vec3> & { root: Vec3; jaw: Vec3; blink: Vec3 };

const vertexShaderSource = `#version 300 es
precision highp float;
in vec3 aPosition;
in vec3 aNormal;
uniform mat4 uModel;
uniform mat4 uViewProjection;
out vec3 vNormal;
out vec3 vWorldPosition;
void main(){
  vec4 world = uModel * vec4(aPosition, 1.0);
  vWorldPosition = world.xyz;
  vNormal = normalize(mat3(uModel) * aNormal);
  gl_Position = uViewProjection * world;
}`;

const fragmentShaderSource = `#version 300 es
precision highp float;
in vec3 vNormal;
in vec3 vWorldPosition;
uniform vec3 uColor;
uniform float uEmissive;
uniform vec3 uLightDirection;
out vec4 outColor;
void main(){
  float diffuse = max(dot(normalize(vNormal), normalize(-uLightDirection)), 0.0);
  float rim = pow(1.0 - max(dot(normalize(vNormal), normalize(vec3(0.0,0.25,1.0))), 0.0), 2.4);
  vec3 lit = uColor * (0.28 + diffuse * 0.72) + uColor * rim * 0.22 + uColor * uEmissive;
  outColor = vec4(lit, 1.0);
}`;

const PALETTES: Record<SceneTheme, { background: Vec3; floor: Vec3; wall: Vec3; accent: Vec3; secondary: Vec3 }> = {
  home: { background: [0.015, 0.025, 0.055], floor: [0.055, 0.07, 0.105], wall: [0.035, 0.055, 0.095], accent: [0.12, 0.72, 1], secondary: [1, 0.52, 0.2] },
  office: { background: [0.01, 0.02, 0.045], floor: [0.045, 0.06, 0.09], wall: [0.03, 0.045, 0.08], accent: [0.12, 0.65, 1], secondary: [0.45, 0.25, 1] },
  airport: { background: [0.015, 0.03, 0.055], floor: [0.08, 0.1, 0.13], wall: [0.04, 0.075, 0.115], accent: [0.05, 0.8, 1], secondary: [1, 0.62, 0.14] },
  restaurant: { background: [0.03, 0.018, 0.035], floor: [0.085, 0.055, 0.075], wall: [0.08, 0.035, 0.065], accent: [1, 0.35, 0.58], secondary: [1, 0.68, 0.22] },
  park: { background: [0.01, 0.035, 0.045], floor: [0.035, 0.11, 0.095], wall: [0.025, 0.075, 0.08], accent: [0.2, 1, 0.72], secondary: [0.12, 0.65, 1] },
  hotel: { background: [0.025, 0.02, 0.055], floor: [0.075, 0.06, 0.11], wall: [0.055, 0.04, 0.095], accent: [0.58, 0.38, 1], secondary: [0.2, 0.82, 1] },
  interview: { background: [0.012, 0.022, 0.045], floor: [0.045, 0.055, 0.085], wall: [0.025, 0.045, 0.085], accent: [0.15, 0.72, 1], secondary: [0.35, 0.95, 0.8] },
  paris_museum: { background: [0.018, 0.018, 0.04], floor: [0.075, 0.07, 0.095], wall: [0.055, 0.045, 0.075], accent: [0.25, 0.75, 1], secondary: [1, 0.72, 0.25] },
  custom: { background: [0.018, 0.02, 0.05], floor: [0.055, 0.06, 0.1], wall: [0.035, 0.045, 0.085], accent: [0.38, 0.65, 1], secondary: [0.72, 0.35, 1] }
};

const RIG: RigNode[] = [
  { name: "pelvis", parent: null, basePosition: [0, 1.02, 0], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0, 0], scale: [0.38, 0.24, 0.24], color: [0.22, 0.25, 0.31] } },
  { name: "spine", parent: "pelvis", basePosition: [0, 0.24, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, 0.28, 0], scale: [0.25, 0.3, 0.2], color: [0.12, 0.15, 0.2] } },
  { name: "chest", parent: "spine", basePosition: [0, 0.52, 0], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0.15, 0], scale: [0.56, 0.42, 0.28], color: [0.72, 0.76, 0.84] } },
  { name: "neck", parent: "chest", basePosition: [0, 0.48, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, 0.12, 0], scale: [0.15, 0.16, 0.15], color: [0.12, 0.14, 0.18] } },
  { name: "head", parent: "neck", basePosition: [0, 0.3, 0], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0.06, 0], scale: [0.34, 0.42, 0.31], color: [0.75, 0.8, 0.88] } },
  { name: "visor", parent: "head", basePosition: [0, 0.09, 0.275], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0, 0], scale: [0.28, 0.2, 0.055], color: [0.015, 0.035, 0.055] } },
  { name: "eyeL", parent: "visor", basePosition: [-0.105, 0.035, 0.055], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0, 0], scale: [0.065, 0.022, 0.016], color: [0.05, 0.85, 1], emissive: 1.45 } },
  { name: "eyeR", parent: "visor", basePosition: [0.105, 0.035, 0.055], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, 0, 0], scale: [0.065, 0.022, 0.016], color: [0.05, 0.85, 1], emissive: 1.45 } },
  { name: "jaw", parent: "head", basePosition: [0, -0.17, 0.24], baseRotation: [0, 0, 0], mesh: { geometry: "cube", offset: [0, 0, 0], scale: [0.15, 0.022, 0.04], color: [0.05, 0.85, 1], emissive: 1.1 } },

  { name: "shoulderL", parent: "chest", basePosition: [-0.53, 0.3, 0], baseRotation: [0, 0, 0] },
  { name: "upperArmL", parent: "shoulderL", basePosition: [0, 0, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.31, 0], scale: [0.15, 0.34, 0.15], color: [0.68, 0.72, 0.8] } },
  { name: "forearmL", parent: "upperArmL", basePosition: [0, -0.63, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.28, 0], scale: [0.13, 0.3, 0.13], color: [0.58, 0.64, 0.73] } },
  { name: "handL", parent: "forearmL", basePosition: [0, -0.56, 0], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, -0.08, 0], scale: [0.15, 0.18, 0.1], color: [0.2, 0.23, 0.28] } },

  { name: "shoulderR", parent: "chest", basePosition: [0.53, 0.3, 0], baseRotation: [0, 0, 0] },
  { name: "upperArmR", parent: "shoulderR", basePosition: [0, 0, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.31, 0], scale: [0.15, 0.34, 0.15], color: [0.68, 0.72, 0.8] } },
  { name: "forearmR", parent: "upperArmR", basePosition: [0, -0.63, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.28, 0], scale: [0.13, 0.3, 0.13], color: [0.58, 0.64, 0.73] } },
  { name: "handR", parent: "forearmR", basePosition: [0, -0.56, 0], baseRotation: [0, 0, 0], mesh: { geometry: "sphere", offset: [0, -0.08, 0], scale: [0.15, 0.18, 0.1], color: [0.2, 0.23, 0.28] } },

  { name: "hipL", parent: "pelvis", basePosition: [-0.23, -0.12, 0], baseRotation: [0, 0, 0] },
  { name: "thighL", parent: "hipL", basePosition: [0, 0, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.4, 0], scale: [0.18, 0.43, 0.18], color: [0.62, 0.67, 0.75] } },
  { name: "shinL", parent: "thighL", basePosition: [0, -0.79, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.37, 0], scale: [0.15, 0.4, 0.15], color: [0.5, 0.56, 0.65] } },
  { name: "footL", parent: "shinL", basePosition: [0, -0.72, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cube", offset: [0, -0.05, 0.12], scale: [0.18, 0.12, 0.32], color: [0.17, 0.2, 0.25] } },

  { name: "hipR", parent: "pelvis", basePosition: [0.23, -0.12, 0], baseRotation: [0, 0, 0] },
  { name: "thighR", parent: "hipR", basePosition: [0, 0, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.4, 0], scale: [0.18, 0.43, 0.18], color: [0.62, 0.67, 0.75] } },
  { name: "shinR", parent: "thighR", basePosition: [0, -0.79, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cylinder", offset: [0, -0.37, 0], scale: [0.15, 0.4, 0.15], color: [0.5, 0.56, 0.65] } },
  { name: "footR", parent: "shinR", basePosition: [0, -0.72, 0], baseRotation: [0, 0, 0], mesh: { geometry: "cube", offset: [0, -0.05, 0.12], scale: [0.18, 0.12, 0.32], color: [0.17, 0.2, 0.25] } }
];

function createShader(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Não foi possível criar o shader.");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || "Erro ao compilar shader.");
  return shader;
}

function createProgram(gl: WebGL2RenderingContext) {
  const program = gl.createProgram();
  if (!program) throw new Error("Não foi possível criar o programa WebGL.");
  gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vertexShaderSource));
  gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource));
  gl.bindAttribLocation(program, 0, "aPosition");
  gl.bindAttribLocation(program, 1, "aNormal");
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || "Erro ao vincular shaders.");
  return program;
}

function geometryFromArrays(gl: WebGL2RenderingContext, positions: number[], normals: number[]): Geometry {
  const position = gl.createBuffer();
  const normal = gl.createBuffer();
  if (!position || !normal) throw new Error("Não foi possível criar buffers WebGL.");
  gl.bindBuffer(gl.ARRAY_BUFFER, position);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
  gl.bindBuffer(gl.ARRAY_BUFFER, normal);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);
  return { position, normal, count: positions.length / 3 };
}

function cubeGeometry(gl: WebGL2RenderingContext): Geometry {
  const p = [
    -1,-1,1, 1,-1,1, 1,1,1, -1,-1,1, 1,1,1, -1,1,1,
    1,-1,-1, -1,-1,-1, -1,1,-1, 1,-1,-1, -1,1,-1, 1,1,-1,
    -1,-1,-1, -1,-1,1, -1,1,1, -1,-1,-1, -1,1,1, -1,1,-1,
    1,-1,1, 1,-1,-1, 1,1,-1, 1,-1,1, 1,1,-1, 1,1,1,
    -1,1,1, 1,1,1, 1,1,-1, -1,1,1, 1,1,-1, -1,1,-1,
    -1,-1,-1, 1,-1,-1, 1,-1,1, -1,-1,-1, 1,-1,1, -1,-1,1
  ];
  const n: number[] = [];
  const normals: Vec3[] = [[0,0,1],[0,0,-1],[-1,0,0],[1,0,0],[0,1,0],[0,-1,0]];
  normals.forEach((normal) => { for (let i=0;i<6;i++) n.push(...normal); });
  return geometryFromArrays(gl, p, n);
}

function sphereGeometry(gl: WebGL2RenderingContext, lat = 16, lon = 22): Geometry {
  const p: number[] = [];
  const n: number[] = [];
  const point = (a: number, b: number): Vec3 => [Math.sin(a)*Math.cos(b), Math.cos(a), Math.sin(a)*Math.sin(b)];
  for (let y=0;y<lat;y++) {
    const a0 = Math.PI*y/lat, a1 = Math.PI*(y+1)/lat;
    for (let x=0;x<lon;x++) {
      const b0 = Math.PI*2*x/lon, b1 = Math.PI*2*(x+1)/lon;
      const v0=point(a0,b0), v1=point(a1,b0), v2=point(a1,b1), v3=point(a0,b1);
      [v0,v1,v2,v0,v2,v3].forEach((v) => { p.push(...v); n.push(...v); });
    }
  }
  return geometryFromArrays(gl,p,n);
}

function cylinderGeometry(gl: WebGL2RenderingContext, segments = 18): Geometry {
  const p:number[]=[]; const n:number[]=[];
  for(let i=0;i<segments;i++){
    const a0=Math.PI*2*i/segments,a1=Math.PI*2*(i+1)/segments;
    const x0=Math.cos(a0),z0=Math.sin(a0),x1=Math.cos(a1),z1=Math.sin(a1);
    const verts:[[number,number,number],[number,number,number],[number,number,number],[number,number,number],[number,number,number],[number,number,number]]=[
      [x0,-1,z0],[x1,-1,z1],[x1,1,z1],[x0,-1,z0],[x1,1,z1],[x0,1,z0]
    ];
    verts.forEach(v=>{p.push(...v); const len=Math.hypot(v[0],v[2])||1;n.push(v[0]/len,0,v[2]/len);});
    [[0,1,0],[x0,1,z0],[x1,1,z1]].forEach(v=>{p.push(...v);n.push(0,1,0);});
    [[0,-1,0],[x1,-1,z1],[x0,-1,z0]].forEach(v=>{p.push(...v);n.push(0,-1,0);});
  }
  return geometryFromArrays(gl,p,n);
}

function mat4Identity(): Mat4 { const m=new Float32Array(16);m[0]=m[5]=m[10]=m[15]=1;return m; }
function mat4Multiply(a:Mat4,b:Mat4):Mat4{const o=new Float32Array(16);for(let c=0;c<4;c++)for(let r=0;r<4;r++)o[c*4+r]=a[0*4+r]*b[c*4+0]+a[1*4+r]*b[c*4+1]+a[2*4+r]*b[c*4+2]+a[3*4+r]*b[c*4+3];return o;}
function mat4Translation(v:Vec3):Mat4{const m=mat4Identity();m[12]=v[0];m[13]=v[1];m[14]=v[2];return m;}
function mat4Scale(v:Vec3):Mat4{const m=mat4Identity();m[0]=v[0];m[5]=v[1];m[10]=v[2];return m;}
function mat4RotationX(a:number):Mat4{const m=mat4Identity(),c=Math.cos(a),s=Math.sin(a);m[5]=c;m[6]=s;m[9]=-s;m[10]=c;return m;}
function mat4RotationY(a:number):Mat4{const m=mat4Identity(),c=Math.cos(a),s=Math.sin(a);m[0]=c;m[2]=-s;m[8]=s;m[10]=c;return m;}
function mat4RotationZ(a:number):Mat4{const m=mat4Identity(),c=Math.cos(a),s=Math.sin(a);m[0]=c;m[1]=s;m[4]=-s;m[5]=c;return m;}
function mat4TRS(position:Vec3,rotation:Vec3,scale:Vec3=[1,1,1]):Mat4{return mat4Multiply(mat4Multiply(mat4Multiply(mat4Multiply(mat4Translation(position),mat4RotationY(rotation[1])),mat4RotationX(rotation[0])),mat4RotationZ(rotation[2])),mat4Scale(scale));}
function mat4Perspective(fov:number,aspect:number,near:number,far:number):Mat4{const f=1/Math.tan(fov/2),m=new Float32Array(16);m[0]=f/aspect;m[5]=f;m[10]=(far+near)/(near-far);m[11]=-1;m[14]=(2*far*near)/(near-far);return m;}
function normalize(v:Vec3):Vec3{const l=Math.hypot(...v)||1;return[v[0]/l,v[1]/l,v[2]/l];}
function cross(a:Vec3,b:Vec3):Vec3{return[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];}
function subtract(a:Vec3,b:Vec3):Vec3{return[a[0]-b[0],a[1]-b[1],a[2]-b[2]];}
function mat4LookAt(eye:Vec3,target:Vec3,up:Vec3=[0,1,0]):Mat4{const z=normalize(subtract(eye,target)),x=normalize(cross(up,z)),y=cross(z,x),m=mat4Identity();m[0]=x[0];m[4]=x[1];m[8]=x[2];m[1]=y[0];m[5]=y[1];m[9]=y[2];m[2]=z[0];m[6]=z[1];m[10]=z[2];m[12]=-(x[0]*eye[0]+x[1]*eye[1]+x[2]*eye[2]);m[13]=-(y[0]*eye[0]+y[1]*eye[1]+y[2]*eye[2]);m[14]=-(z[0]*eye[0]+z[1]*eye[1]+z[2]*eye[2]);return m;}

function vecLerp(a:Vec3,b:Vec3,t:number):Vec3{return[a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t,a[2]+(b[2]-a[2])*t];}
function emptyPose():Pose{const pose:Record<string,Vec3>={};RIG.forEach(n=>pose[n.name]=[0,0,0]);return Object.assign(pose,{root:[0,0,0] as Vec3,jaw:[0,0,0] as Vec3,blink:[0,0,0] as Vec3}) as Pose;}
function setRot(pose:Pose,name:string,x:number,y:number,z:number){pose[name]=[x,y,z];}

function speechEnvelope(time:number,text:string){
  if(!text) return 0;
  const speed=11.5;
  const index=Math.floor(time*speed)%Math.max(1,text.length);
  const c=(text[index]||" ").toLowerCase();
  if(/[.!?,;:\s]/.test(c)) return 0.08;
  if(/[aeiouáéíóúàèìòùâêîôûãõü]/.test(c)) return 0.72+Math.sin(time*34)*0.16;
  return 0.32+Math.sin(time*27+index)*0.1;
}

function poseFor(state:MotionState,time:number,text:string,stance:"walking"|"sitting"|"standing"):Pose{
  const p=emptyPose();
  const breathe=Math.sin(time*1.6)*0.018;
  p.root=[0,breathe,0];
  setRot(p,"spine",breathe*0.8,Math.sin(time*.36)*.018,Math.sin(time*.55)*.012);
  setRot(p,"head",Math.sin(time*.7)*.018,Math.sin(time*.43)*.035,Math.sin(time*.51)*.015);
  const blink=(Math.sin(time*.82)+Math.sin(time*1.73+1.2)>1.68)?0.92:0;
  p.blink=[blink,0,0];
  switch(state){
    case "greeting": {
      const w=Math.sin(time*7)*.38;
      setRot(p,"upperArmR",-1.0,0,.2);
      setRot(p,"forearmR",-1.05,0,-.42+w);
      setRot(p,"handR",0,0,w*.7);
      setRot(p,"head",-.08,.08,.08);
      break;
    }
    case "talking": {
      const beat=Math.sin(time*2.2),beat2=Math.sin(time*1.3+1.4);
      setRot(p,"upperArmR",-.35+beat*.12,0,-.22);
      setRot(p,"forearmR",-.8+beat2*.18,0,.16);
      setRot(p,"upperArmL",-.18-beat2*.08,0,.12);
      setRot(p,"forearmL",-.48+beat*.12,0,-.12);
      setRot(p,"head",-.015+Math.sin(time*1.1)*.03,Math.sin(time*.72)*.055,Math.sin(time*.93)*.02);
      p.jaw=[speechEnvelope(time,text),0,0];
      break;
    }
    case "listening": {
      setRot(p,"head",.04,.09,.075);
      setRot(p,"upperArmL",-.15,0,.1);
      setRot(p,"forearmL",-.48,0,-.24);
      setRot(p,"upperArmR",-.15,0,-.1);
      setRot(p,"forearmR",-.48,0,.24);
      break;
    }
    case "walking": {
      const cycle=time*3.3,s=Math.sin(cycle),c=Math.cos(cycle);
      p.root=[Math.sin(time*.42)*.28,Math.abs(s)*.045,0];
      setRot(p,"thighL",s*.55,0,0); setRot(p,"thighR",-s*.55,0,0);
      setRot(p,"shinL",Math.max(0,-s)*.78,0,0); setRot(p,"shinR",Math.max(0,s)*.78,0,0);
      setRot(p,"upperArmL",-s*.42,0,.08); setRot(p,"upperArmR",s*.42,0,-.08);
      setRot(p,"forearmL",-.18+Math.max(0,s)*.2,0,0); setRot(p,"forearmR",-.18+Math.max(0,-s)*.2,0,0);
      setRot(p,"spine",.03,Math.sin(time*.42)*.06,-c*.018);
      break;
    }
    case "sitting": {
      p.root=[0,-.48,.1];
      setRot(p,"pelvis",-.08,0,0);setRot(p,"thighL",-1.42,0,0);setRot(p,"thighR",-1.42,0,0);
      setRot(p,"shinL",1.35,0,0);setRot(p,"shinR",1.35,0,0);
      setRot(p,"upperArmL",-.22,0,.12);setRot(p,"forearmL",-.72,0,-.15);
      setRot(p,"upperArmR",-.22,0,-.12);setRot(p,"forearmR",-.72,0,.15);
      break;
    }
    case "presenting": {
      setRot(p,"head",-.02,-.18,-.02);
      setRot(p,"upperArmR",-.62,-.15,-.72);
      setRot(p,"forearmR",-.45,.05,-.2);
      setRot(p,"handR",0,.1,-.08);
      setRot(p,"upperArmL",-.12,0,.08); setRot(p,"forearmL",-.38,0,-.08);
      break;
    }
    case "celebrating": {
      const dance=Math.sin(time*4.6),dance2=Math.sin(time*4.6+Math.PI);
      p.root=[dance*.12,Math.abs(dance)*.05,0];
      setRot(p,"spine",0,dance*.14,dance*.08);
      setRot(p,"upperArmL",-1.05+dance*.22,0,.72);setRot(p,"upperArmR",-1.05+dance2*.22,0,-.72);
      setRot(p,"forearmL",-.55+dance2*.35,0,-.2);setRot(p,"forearmR",-.55+dance*.35,0,.2);
      setRot(p,"head",-.06,dance*.09,dance*.06);
      break;
    }
    default: {
      setRot(p,"upperArmL",-.08,0,.08);setRot(p,"upperArmR",-.08,0,-.08);
      setRot(p,"forearmL",-.12,0,0);setRot(p,"forearmR",-.12,0,0);
    }
  }
  // Mantém a postura do cenário também durante a fala. Assim AURA não se
  // levanta artificialmente quando conversa sentada e pode falar caminhando.
  if(stance==="sitting" && state!=="sitting" && state!=="walking" && state!=="celebrating") {
    p.root=[p.root[0],p.root[1]-.48,p.root[2]+.1];
    setRot(p,"pelvis",-.08,0,0);setRot(p,"thighL",-1.42,0,0);setRot(p,"thighR",-1.42,0,0);
    setRot(p,"shinL",1.35,0,0);setRot(p,"shinR",1.35,0,0);
  } else if(stance==="walking" && (state==="talking" || state==="listening" || state==="presenting")) {
    const cycle=time*2.55,s=Math.sin(cycle);
    p.root=[p.root[0]+Math.sin(time*.35)*.2,p.root[1]+Math.abs(s)*.035,p.root[2]];
    setRot(p,"thighL",s*.38,0,0);setRot(p,"thighR",-s*.38,0,0);
    setRot(p,"shinL",Math.max(0,-s)*.55,0,0);setRot(p,"shinR",Math.max(0,s)*.55,0,0);
  }
  return p;
}

function inferMotion(session:LearningSession,speakingSide:SpeakingSide,listening:boolean,text:string,emotion?:string|null):MotionState{
  const normalized=`${emotion||""} ${text}`.toLowerCase();
  if(speakingSide==="user"||listening) return "listening";
  if(speakingSide==="ai"){
    if(/parabéns|excelente|great|excellent|muy bien|très bien|太好了|celebr/.test(normalized)) return "celebrating";
    if(/olhe|observe|veja|look|regardez|mira|看|观察|mostr/.test(normalized)) return "presenting";
    return "talking";
  }
  if(session.sceneAction==="walking") return "walking";
  if(session.sceneAction==="sitting") return "sitting";
  return "idle";
}

function motionLabel(state:MotionState){
  const labels:Record<MotionState,string>={idle:"AURA está pronta",greeting:"AURA está cumprimentando",talking:"AURA está falando",listening:"AURA está ouvindo",walking:"AURA caminha pelo cenário",sitting:"AURA está sentada",presenting:"AURA apresenta o ambiente",celebrating:"AURA comemora seu progresso"};
  return labels[state];
}

function initRenderer(canvas:HTMLCanvasElement):RenderContext{
  const gl=canvas.getContext("webgl2",{antialias:true,alpha:true,powerPreference:"high-performance"});
  if(!gl) throw new Error("Este dispositivo não oferece WebGL 2. Use Chrome, Edge ou Firefox atualizado com aceleração gráfica ativada.");
  const program=createProgram(gl);
  gl.useProgram(program);
  const model=gl.getUniformLocation(program,"uModel"),viewProjection=gl.getUniformLocation(program,"uViewProjection"),color=gl.getUniformLocation(program,"uColor"),emissive=gl.getUniformLocation(program,"uEmissive"),lightDirection=gl.getUniformLocation(program,"uLightDirection");
  if(!model||!viewProjection||!color||!emissive||!lightDirection) throw new Error("Uniformes WebGL incompletos.");
  gl.enable(gl.DEPTH_TEST);gl.enable(gl.CULL_FACE);gl.cullFace(gl.BACK);
  return{gl,program,cube:cubeGeometry(gl),sphere:sphereGeometry(gl),cylinder:cylinderGeometry(gl),uniforms:{model,viewProjection,color,emissive,lightDirection}};
}

function drawMesh(ctx:RenderContext,geometry:Geometry,model:Mat4,color:Vec3,emissive=0){
  const{gl,uniforms}=ctx;
  gl.uniformMatrix4fv(uniforms.model,false,model);gl.uniform3fv(uniforms.color,color);gl.uniform1f(uniforms.emissive,emissive);
  gl.bindBuffer(gl.ARRAY_BUFFER,geometry.position);gl.enableVertexAttribArray(0);gl.vertexAttribPointer(0,3,gl.FLOAT,false,0,0);
  gl.bindBuffer(gl.ARRAY_BUFFER,geometry.normal);gl.enableVertexAttribArray(1);gl.vertexAttribPointer(1,3,gl.FLOAT,false,0,0);
  gl.drawArrays(gl.TRIANGLES,0,geometry.count);
}

function drawBox(ctx:RenderContext,position:Vec3,scale:Vec3,color:Vec3,rotation:Vec3=[0,0,0],emissive=0){drawMesh(ctx,ctx.cube,mat4TRS(position,rotation,scale),color,emissive);}

function drawEnvironment(ctx:RenderContext,theme:SceneTheme,time:number){
  const p=PALETTES[theme]||PALETTES.custom;
  drawBox(ctx,[0,-.12,-.2],[4.6,.08,4.5],p.floor);
  drawBox(ctx,[0,2.15,-3.05],[4.6,2.25,.08],p.wall);
  drawBox(ctx,[-4.55,2.1,-.2],[.08,2.2,4.5],p.wall);
  drawBox(ctx,[4.55,2.1,-.2],[.08,2.2,4.5],p.wall);
  for(let i=-4;i<=4;i++) drawBox(ctx,[i*.95,.005,-.2],[.014,.01,4.45],p.accent,[0,0,0],.32);
  for(let i=0;i<5;i++) drawBox(ctx,[-3.65+i*1.82,3.72,-2.94],[.64,.035,.035],i%2?p.secondary:p.accent,[0,0,0],.75);
  const pulse=.65+Math.sin(time*.8)*.2;
  if(theme==="paris_museum"){
    for(let i=-2;i<=2;i++){
      const x=i*1.45;drawBox(ctx,[x,2.05,-2.92],[.55,.78,.035],[.12,.085,.06]);drawBox(ctx,[x,2.05,-2.82],[.42,.62,.025],i%2?p.secondary:p.accent,[0,0,0],.18);
    }
    drawBox(ctx,[2.8,.45,-1.5],[.55,.45,.55],[.12,.13,.17]);
  }else if(theme==="airport"){
    drawBox(ctx,[0,2.45,-2.86],[2.5,.52,.035],[.025,.055,.095]);
    for(let i=0;i<6;i++) drawBox(ctx,[-1.95+i*.78,2.45,-2.78],[.26,.03,.02],p.secondary,[0,0,0],pulse);
    drawBox(ctx,[2.75,1.15,-2.3],[.8,1.1,.08],[.035,.07,.11]);
  }else if(theme==="home"||theme==="restaurant"||theme==="hotel"){
    drawBox(ctx,[-2.4,.48,-2.15],[1.25,.48,.55],[.08,.095,.12]);
    drawBox(ctx,[2.5,.42,-1.85],[1.05,.42,.7],[.075,.085,.11]);
    drawBox(ctx,[2.5,.92,-1.85],[.48,.035,.48],p.secondary,[0,0,0],.18);
  }else if(theme==="park"){
    for(let i=-3;i<=3;i+=2){drawBox(ctx,[i,0.55,-2.3],[.12,.55,.12],[.12,.25,.18]);drawMesh(ctx,ctx.sphere,mat4TRS([i,1.45,-2.3],[0,0,0],[.62,.72,.62]),[.08,.42,.27],0);}
  }else{
    drawBox(ctx,[2.45,1.65,-2.82],[1.35,.82,.035],[.025,.065,.105]);
    for(let i=0;i<5;i++) drawBox(ctx,[1.45+i*.5,1.34+i%2*.15,-2.72],[.12,.015,.02],p.accent,[0,0,0],pulse);
    drawBox(ctx,[-2.4,.58,-1.9],[1.1,.58,.6],[.065,.075,.105]);
  }
  // Plataforma e assento holográfico usados quando AURA se senta.
  drawBox(ctx,[0,.07,.25],[.65,.08,.65],p.accent,[0,0,0],.18);
  drawBox(ctx,[0,.48,.2],[.36,.38,.36],[.055,.07,.1]);
}

function drawRobot(ctx:RenderContext,pose:Pose,current:Pose){
  const damping=.095;
  Object.keys(current).forEach((key)=>{current[key]=vecLerp(current[key],pose[key]||[0,0,0],damping);});
  const world=new Map<string,Mat4>();
  const rootMatrix=mat4TRS(current.root,[0,0,0]);
  for(const node of RIG){
    const parent=node.parent?world.get(node.parent)||rootMatrix:rootMatrix;
    const animated=current[node.name]||[0,0,0];
    const local=mat4TRS(node.basePosition,[node.baseRotation[0]+animated[0],node.baseRotation[1]+animated[1],node.baseRotation[2]+animated[2]]);
    const nodeWorld=mat4Multiply(parent,local);world.set(node.name,nodeWorld);
    if(node.mesh){
      const geometry=node.mesh.geometry==="cube"?ctx.cube:node.mesh.geometry==="sphere"?ctx.sphere:ctx.cylinder;
      const extra=node.name==="jaw"?[0,-current.jaw[0]*.035,0] as Vec3:node.name.startsWith("eye")?[0,0,0] as Vec3:[0,0,0] as Vec3;
      const scale=node.name.startsWith("eye")?[node.mesh.scale[0],node.mesh.scale[1]*(1-current.blink[0]*.92),node.mesh.scale[2]] as Vec3:node.mesh.scale;
      const meshMatrix=mat4Multiply(nodeWorld,mat4TRS([node.mesh.offset[0]+extra[0],node.mesh.offset[1]+extra[1],node.mesh.offset[2]+extra[2]],[0,0,0],scale));
      drawMesh(ctx,geometry,meshMatrix,node.mesh.color,node.mesh.emissive||0);
    }
  }
  // Núcleo e linhas luminosas no tórax.
  const chest=world.get("chest");
  if(chest){
    drawMesh(ctx,ctx.sphere,mat4Multiply(chest,mat4TRS([0,.12,.285],[0,0,0],[.12,.12,.035])),[.05,.75,1],1.15);
    drawMesh(ctx,ctx.cube,mat4Multiply(chest,mat4TRS([0,-.08,.29],[0,0,0],[.24,.018,.025])),[.05,.75,1],.8);
  }
}

export function AuraWebGLScene({session,speakingSide,listening,assistantText="",userText="",emotion}:SceneProps){
  const canvasRef=useRef<HTMLCanvasElement>(null);
  const frameRef=useRef<number>(0);
  const currentPose=useRef<Pose>(emptyPose());
  const startTime=useRef(performance.now());
  const [renderError,setRenderError]=useState("");
  const [greeting,setGreeting]=useState(true);
  const previousAssistant=useRef(assistantText);

  useEffect(()=>{const timer=window.setTimeout(()=>setGreeting(false),2800);return()=>window.clearTimeout(timer);},[]);
  useEffect(()=>{if(!assistantText||assistantText===previousAssistant.current)return;previousAssistant.current=assistantText;setGreeting(true);const timer=window.setTimeout(()=>setGreeting(false),1300);return()=>window.clearTimeout(timer);},[assistantText]);

  const inferred=useMemo(()=>inferMotion(session,speakingSide,listening,assistantText,emotion),[session,speakingSide,listening,assistantText,emotion]);
  const state:MotionState=greeting&&!speakingSide?"greeting":inferred;
  const stateRef=useRef(state);const textRef=useRef(assistantText);const themeRef=useRef(session.sceneTheme);const stanceRef=useRef(session.sceneAction);
  useEffect(()=>{stateRef.current=state;textRef.current=assistantText;themeRef.current=session.sceneTheme;stanceRef.current=session.sceneAction;startTime.current=performance.now();},[state,assistantText,session.sceneTheme,session.sceneAction]);

  useEffect(()=>{
    const canvas=canvasRef.current;if(!canvas)return;
    let ctx:RenderContext;
    try{ctx=initRenderer(canvas);setRenderError("");}catch(error){setRenderError(error instanceof Error?error.message:"Falha ao iniciar o renderizador 3D.");return;}
    const resize=()=>{
      const ratio=Math.min(window.devicePixelRatio||1,2);
      const width=Math.max(1,Math.floor(canvas.clientWidth*ratio)),height=Math.max(1,Math.floor(canvas.clientHeight*ratio));
      if(canvas.width!==width||canvas.height!==height){canvas.width=width;canvas.height=height;ctx.gl.viewport(0,0,width,height);}
    };
    const render=(now:number)=>{
      resize();
      const {gl,program,uniforms}=ctx;const theme=themeRef.current;const palette=PALETTES[theme]||PALETTES.custom;
      gl.clearColor(...palette.background,1);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);gl.useProgram(program);
      const aspect=canvas.width/Math.max(1,canvas.height);const projection=mat4Perspective(Math.PI/4.15,aspect,.1,50);
      const stateNow=stateRef.current;const cameraZ=stateNow==="walking"?4.75:stateNow==="sitting"?4.55:4.45;
      const view=mat4LookAt([0,1.55,cameraZ],[0,1.35,0],[0,1,0]);const vp=mat4Multiply(projection,view);
      gl.uniformMatrix4fv(uniforms.viewProjection,false,vp);gl.uniform3fv(uniforms.lightDirection,new Float32Array([-.35,-.9,-.55]));
      const t=(now-startTime.current)/1000;drawEnvironment(ctx,theme,now/1000);drawRobot(ctx,poseFor(stateNow,t,textRef.current,stanceRef.current),currentPose.current);
      frameRef.current=requestAnimationFrame(render);
    };
    frameRef.current=requestAnimationFrame(render);
    return()=>{cancelAnimationFrame(frameRef.current);ctx.gl.getExtension("WEBGL_lose_context")?.loseContext();};
  },[]);

  const shortAssistant=(assistantText||"Olá! Eu sou AURA. Estou pronta para conversar com você neste cenário.").slice(0,240);
  return <section className={`aura-webgl-scene state-${state}`}>
    <canvas ref={canvasRef} className="aura-webgl-canvas" aria-label={`AURA em cenário 3D: ${session.scenarioTitle}`} />
    {renderError&&<div className="aura-webgl-error"><b>Renderização 3D indisponível</b><p>{renderError}</p></div>}
    <div className="aura-webgl-vignette" />
    <div className="aura-stage-topbar">
      <div className="aura-location-chip"><span>{session.scenarioIcon}</span><div><b>{session.scenarioTitle}</b><small>Ambiente 3D em tempo real · visão do aluno</small></div></div>
      <div className={`aura-live-chip ${speakingSide==="ai"?"speaking":listening||speakingSide==="user"?"listening":"idle"}`}><i/><Waves/><span>{motionLabel(state)}</span></div>
      <div className="aura-engine-chip"><Sparkles/><span>Avatar esquelético 3D</span></div>
    </div>
    <div className="aura-dialogue-overlay"><div className="aura-avatar-mark"><span>A</span><i/></div><div><b>AURA · tutor robótico</b><p>{shortAssistant}</p>{userText&&speakingSide==="user"&&<small>Você: {userText.slice(0,110)}</small>}</div><Volume2/></div>
    <div className="aura-provider-chip"><i/>{session.aiProvider === "groq" ? "GroqCloud" : "Google Gemini"} · {session.aiModel} · conversa online sem fallback</div>
  </section>;
}
